import { Link } from 'react-router';
import { AlertTriangle, ArrowRight, Clock3, CheckCircle2 } from 'lucide-react';
import { useClubReadiness } from '../hooks/useClubReadiness';

/**
 * Renders an actionable banner above the club pages whenever the
 * active club isn't fully set up.
 *
 * Three states:
 *   - blocking    → red/amber, list of missing setup steps
 *   - pending     → blue, "we're reviewing your application"
 *   - ready+active → nothing rendered
 *
 * Each item links to the partner page that fixes it.
 */
export default function ClubReadinessBanner({ clubId }: { clubId: string | null }) {
  const r = useClubReadiness(clubId);
  if (r.loading) return null;
  if (r.error) {
    // Don't block the page — render a quiet inline notice so staff
    // know readiness data didn't load (network / RPC failure) rather
    // than us silently pretending everything is fine.
    return (
      <div className="border-b border-amber-200 bg-amber-50">
        <div className="max-w-5xl mx-auto px-6 py-2 flex items-center gap-2 text-xs text-amber-900">
          <AlertTriangle className="w-3.5 h-3.5" />
          Couldn't check club readiness ({r.error}). The page is fine to use; refresh to retry.
        </div>
      </div>
    );
  }
  if (r.missing.length === 0) return null;

  const onlyPending = r.missing.length === 1 && r.missing[0].kind === 'pending_activation';

  if (onlyPending) {
    const item = r.missing[0];
    return (
      <div className="border-b border-blue-200 bg-blue-50">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-3 text-sm">
          <Clock3 className="w-4 h-4 text-blue-600 shrink-0" />
          <div className="flex-1">
            <span className="font-medium text-blue-900">{item.label}.</span>{' '}
            <span className="text-blue-800">{item.description}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-amber-200 bg-amber-50">
      <div className="max-w-5xl mx-auto px-6 py-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900 text-sm mb-1">
              {r.missing.length === 1
                ? 'Finish 1 step to start taking bookings'
                : `Finish ${r.missing.length} steps to start taking bookings`}
            </div>
            <p className="text-xs text-amber-800 mb-3">
              Customers can't book this club until setup is complete and AceRent activates it.
            </p>
            <ul className="space-y-1.5">
              {r.missing.map((m) => (
                <li key={m.kind}>
                  <Link
                    to={m.href}
                    className="inline-flex items-center gap-2 text-sm text-amber-900 font-medium hover:text-amber-700 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover:bg-amber-700" />
                    {m.label}
                    <ArrowRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                    <span className="text-xs text-amber-700 font-normal">— {m.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Small inline pill for places where we want a tighter reminder than
 * the full banner. Use on action buttons that are about to fire a
 * write that wouldn't make sense pre-activation.
 */
export function ClubReadinessChip({ clubId }: { clubId: string | null }) {
  const r = useClubReadiness(clubId);
  if (r.loading) return null;
  if (r.ready && r.isActive) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700">
        <CheckCircle2 className="w-3.5 h-3.5" /> Live
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700">
      <AlertTriangle className="w-3.5 h-3.5" /> Setup incomplete
    </span>
  );
}
